/**
 * event-gate — UC6 event-gated eligibility (2026-08-13).
 *
 * THE RULE, one place: an events.json row may carry
 *   "unlocks": { "service": "<service name>", "withinDays": 7 }
 * meaning that service is bookable ONLY by people who CHECKED IN at that
 * event. Attendance is read from the event-guests family (status
 * "checked-in"/"attended"), joined by customer NAME — the same durable key
 * every other cross-family join in this workspace uses.
 *
 * Consumers: BookAppointmentSheet (staff-side lock + save guard) today; the
 * public booking surface hides gated services entirely via the same gateFor.
 * UC5 (Orientation) reuses this rule verbatim for its event-gated advisor
 * slots — that was the point of building it here first.
 *
 * Honest-data notes: no events doc or no guests doc ⇒ no gates (never
 * invented); a gate on an event with NO guest rows locks for everyone except
 * via the event's own check-in flow, which is correct, not a bug. The rule
 * belongs on the service record in the real contract — flagged as the REQ ask
 * in the changelog; this module is the UI-side seam it will collapse into.
 */
import { useEffect, useState } from "react";
import { fetchEventsList } from "../../lib/api";
import { apiRaw } from "@v0y4ge/api-client";
import { t as i18nT } from "../../lib/i18n";

export interface ServiceGate {
  eventTitle: string;
  eventId: string;
  withinDays: number;
  /** normalized lowercase names of checked-in attendees */
  attendees: Set<string>;
  allowed: (customerName: string) => boolean;
}

interface GateState { byService: Map<string, ServiceGate>; byQueue: Map<string, ServiceGate>; loaded: boolean }
let _state: GateState | null = null;
let _inFlight: Promise<GateState> | null = null;
const norm = (s: string) => (s || "").trim().toLowerCase();

async function loadGates(): Promise<GateState> {
  const byService = new Map<string, ServiceGate>();
  const byQueue = new Map<string, ServiceGate>();
  try {
    const r = await fetchEventsList({});
    const rows = ((r as { events?: unknown[]; data?: unknown[] })?.events || (r as { data?: unknown[] })?.data || []) as Record<string, unknown>[];
    /* UC5 (2026-08-13): `spawns` joins `unlocks` — an event may also declare
     * spawns:{queue} (an EVENT-SPAWNED QUEUE: check-in at the event is what
     * grants a place in that queue — orientation's ID-card pickup). Same
     * attendee set, same name join, one loader. */
    const gated = rows.filter((e) => (e.unlocks && typeof e.unlocks === "object") || (e.spawns && typeof e.spawns === "object"));
    for (const ev of gated) {
      const u = (ev.unlocks ?? {}) as { service?: string; withinDays?: number };
      const sp = (ev.spawns ?? {}) as { queue?: string };
      if (!u.service && !sp.queue) continue;
      const attendees = new Set<string>();
      try {
        /* apiRaw, not the listEventGuests barrel export: in the partial-kit
         * preview that export can be a barrel-patched shim whose empty result
         * lands in this catch and silently locks the gate for EVERYONE
         * (verifier, 2026-08-13). apiRaw is the same choke point every local
         * api module here uses, and the route is probe-confirmed. */
        const g = await apiRaw<{ data?: unknown[]; guests?: unknown[] }>(`/v1/admin/events/${encodeURIComponent(String(ev.id))}/guests?limit=200`, undefined, "listEventGuests");
        ((((g as { data?: unknown[] }).data ?? (g as { guests?: unknown[] }).guests) || []) as Record<string, unknown>[]).forEach((row) => {
          const st = String(row.status || "");
          if (st === "checked-in" || st === "checked_in" || st === "attended") {
            const nm = String(row.name || `${row.firstName ?? row.first_name ?? ""} ${row.lastName ?? row.last_name ?? ""}`);
            if (nm.trim()) attendees.add(norm(nm));
          }
        });
      } catch { /* guests route absent — gate locks except via check-in, honest */ }
      const gate: ServiceGate = {
        eventTitle: String(ev.title || ""), eventId: String(ev.id), withinDays: u.withinDays ?? 7,
        attendees,
        allowed: (customerName: string) => attendees.has(norm(customerName)),
      };
      if (u.service) byService.set(norm(u.service), gate);
      if (sp.queue) byQueue.set(norm(sp.queue), gate);
    }
  } catch { /* events unavailable — no gates, never a crash */ }
  return { byService, byQueue, loaded: true };
}

export function useEventGate() {
  const [state, setState] = useState<GateState>(_state ?? { byService: new Map(), byQueue: new Map(), loaded: false });
  useEffect(() => {
    let live = true;
    if (_state) { setState(_state); return; }
    if (!_inFlight) _inFlight = loadGates().then((s) => { _state = s; return s; });
    void _inFlight.then((s) => { if (live) setState(s); });
    return () => { live = false; };
  }, []);
  return {
    gateFor: (serviceName: string): ServiceGate | null => state.byService.get(norm(serviceName)) ?? null,
    /* Event-spawned queues (UC5): gate by QUEUE name. */
    queueGateFor: (queueName: string): ServiceGate | null => state.byQueue.get(norm(queueName)) ?? null,
    lockedHint: (g: ServiceGate) => i18nT("appointments.gate.locked", "Reserved for {event} attendees — this customer isn't on the checked-in list.").replace("{event}", g.eventTitle),
    unlockedHint: (g: ServiceGate) => i18nT("appointments.gate.unlocked", "Unlocked — checked in at {event}.").replace("{event}", g.eventTitle),
    /* Neutral hint for a blank customer — claiming "unlocked" before a name
     * exists would be untrue. */
    reservedHint: (g: ServiceGate) => i18nT("appointments.gate.reserved", "Reserved for {event} attendees.").replace("{event}", g.eventTitle),
  };
}
